import { useState, useEffect } from 'react';
import { Card, Button, LoadingSpinner } from '../components/Layout';
import api from '../services/api';

export function RewardsPage() {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<any>(null);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const [balanceData, catalogData, transData, statsData] = await Promise.all([
        api.getPointsBalance(),
        api.getRewardsCatalog({ is_active: true }),
        api.getPointsTransactions({ limit: 10 }),
        api.getMonthlyPointsStats(now.getMonth() + 1, now.getFullYear()),
      ]);
      setBalance(balanceData);
      setCatalog(Array.isArray(catalogData) ? catalogData : []);
      setTransactions(Array.isArray(transData) ? transData : []);
      setMonthlyStats(statsData);
    } catch (error) {
      console.error('Error loading rewards:', error);
      // Ensure safe defaults on error
      setBalance(null);
      setCatalog([]);
      setTransactions([]);
      setMonthlyStats(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <h1 className="text-2xl font-bold text-white mb-4">Rewards</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-gray-400 text-sm">Current Balance</div>
            <div className="text-3xl font-bold text-yellow-400 mt-2">
              {balance?.current_balance || 0}
            </div>
            <div className="text-gray-500 text-sm mt-1">pts</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Earned This Month</div>
            <div className="text-3xl font-bold text-green-400 mt-2">
              +{monthlyStats?.earned_this_month || 0}
            </div>
            <div className="text-gray-500 text-sm mt-1">pts</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Tier</div>
            <div className="text-3xl font-bold text-purple-400 mt-2 capitalize">
              {balance?.tier || 'Bronze'}
            </div>
          </div>
        </div>
      </Card>

      {/* Rewards Catalog */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">Redeem Rewards</h2>
        {catalog.length === 0 ? (
          <p className="text-gray-400 text-center py-4">No rewards available</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {catalog.map((reward) => (
              <div key={reward.id} className="bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="text-white font-medium">{reward.name}</div>
                    {reward.description && (
                      <div className="text-gray-400 text-sm mt-1">{reward.description}</div>
                    )}
                    <div className="text-yellow-400 font-bold mt-2">{reward.points_cost} pts</div>
                  </div>
                  <Button
                    size="sm"
                    disabled={!balance || balance.current_balance < reward.points_cost}
                  >
                    Redeem
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent Activity */}
      {transactions.length > 0 && (
        <Card>
          <h2 className="text-xl font-bold text-white mb-4">Recent Activity</h2>
          <div className="space-y-2">
            {transactions.map((txn) => (
              <div key={txn.id} className="bg-gray-700 p-3 rounded-lg flex justify-between items-center">
                <div>
                  <div className="text-white text-sm">{txn.description}</div>
                  <div className="text-gray-400 text-xs mt-1">
                    {new Date(txn.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className={`font-bold ${txn.transaction_type === 'earned' ? 'text-green-400' : 'text-red-400'}`}>
                  {txn.transaction_type === 'earned' ? '+' : '-'}{txn.points}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
